<template>
  <div class="prompt-simulator">
    <a-page-header title="AI调用模拟" />
    
    <a-row :gutter="16" style="margin-top: 16px">
      <!-- 左侧配置区 -->
      <a-col :span="10">
        <a-card :bordered="false">
          <template #title>
            <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
              <span>配置区（<span :class="historyMode ? 'mode-text-history' : 'mode-text-simulate'">{{ historyMode ? '历史模式' : '模拟模式' }}</span>）</span>
              <a-button 
                size="small" 
                type="default"
                @click="handleClearAllConfig"
              >
                清空配置数据
              </a-button>
            </div>
          </template>
          <!-- 历史计划选择器 -->
          <HistoryPlanSelector
            ref="historyPlanSelectorRef"
            @plan-selected="handlePlanSelected"
            @plan-cleared="handlePlanCleared"
          />
          
          <!-- Worker选择 -->
          <WorkerSelector 
            v-model:selectedFlow="selectedFlow"
            v-model:selectedStep="selectedStep"
            v-model:selectedWorker="selectedWorker"
            :historyMode="historyMode"
            :schemeSnapshot="historySchemeSnapshot"
            @worker-selected="handleWorkerSelected"
          />
          
          <!-- 输入参数表单 -->
          <InputForm 
            v-model:formData="inputFormData"
            :readonly="historyMode"
            @change="handleInputChange"
            style="margin-top: 16px"
          />
          
          <!-- Worker配置的模型（只读展示） -->
          <div v-if="selectedWorker && workerDetail" style="margin-top: 16px">
            <a-form-item label="Worker配置模型">
              <a-input 
                :value="workerModelDisplay" 
                disabled 
                placeholder="Worker未配置模型"
              />
            </a-form-item>
          </div>
          
          <!-- 出参定义 -->
          <OutputSchema 
            v-if="selectedWorker && workerDetail"
            :output-params="workerDetail.outputParams"
            style="margin-top: 16px"
          />
          
          <!-- 生成按钮 -->
          <a-button 
            type="primary" 
            block 
            :loading="generating"
            :disabled="!canGenerate"
            @click="handleGeneratePrompt"
            style="margin-top: 16px"
          >
            生成提示词
          </a-button>
        </a-card>
      </a-col>
      
      <!-- 右侧结果展示区 -->
      <a-col :span="14">
        <!-- Worker配置知识 -->
        <WorkerKnowledgeTypes 
          v-if="selectedWorker && workerDetail"
          :knowledge-types="workerDetail.knowledgeTypes || []"
          :config-knowledge="configKnowledge"
          style="margin-bottom: 16px"
        />
        
        <!-- 输入参数匹配到的知识 -->
        <QueriedKnowledgeList 
          v-if="queriedKnowledge"
          :queried-knowledge="queriedKnowledge"
          :debug-sql="debugSQL"
          :selected-knowledge-id="selectedKnowledgeId"
          @knowledge-click="handleKnowledgeClick"
          style="margin-bottom: 16px"
        />
        
        <!-- 合并后的知识列表 -->
        <MergedKnowledgeList 
          v-if="mergedKnowledge"
          :merged-knowledge="mergedKnowledge"
          :config-knowledge="configKnowledge"
          :selected-knowledge-id="selectedKnowledgeId"
          @knowledge-click="handleKnowledgeClick"
          style="margin-bottom: 16px"
        />
        
        <!-- 额外字段列表（历史模式） -->
        <ExtraFieldsList
          v-if="historyMode && historyPlanData"
          :planData="historyPlanData"
          :available-worker-refs="availableWorkerRefs"
          @field-click="handleExtraFieldClick"
          style="margin-bottom: 16px"
        />
        
        <a-card title="结果展示区" :bordered="false">
          <!-- 提示词展示 -->
          <PromptViewer 
            v-if="xmlPrompt"
            :key="promptKey"
            :prompt="xmlPrompt"
            :knowledge-mapping="knowledgeMapping"
            :selected-knowledge-id="selectedKnowledgeId"
            :selected-placeholder="selectedPlaceholder"
            ref="promptViewerRef"
          />
          
          <!-- 模型选择（用于提交） -->
          <div v-if="xmlPrompt" style="margin-top: 16px">
            <a-form-item label="选择模型">
              <a-select
                v-model:value="selectedModelId"
                placeholder="请选择模型"
                :loading="modelsLoading"
                style="width: 100%"
              >
                <a-select-option v-for="model in availableModels" :key="model.id" :value="model.id">
                  {{ getModelDisplayName(model) }}
                </a-select-option>
              </a-select>
            </a-form-item>
            <a-alert
              v-if="selectedModelSubmitHint"
              :type="selectedModelStructuredOutputSupported ? 'info' : 'warning'"
              :message="selectedModelSubmitHint"
              show-icon
              class="model-submit-hint"
            />
          </div>
          
          <!-- 提交按钮 -->
          <a-button 
            v-if="xmlPrompt"
            type="primary" 
            block 
            :loading="callingModel"
            :disabled="!selectedModelId"
            @click="handleCallModel"
            style="margin-top: 8px"
          >
            提交到模型
          </a-button>
          
          <!-- 模型响应展示 -->
          <ResponseViewer 
            v-if="modelResponse"
            :response="modelResponse"
            style="margin-top: 16px"
          />
          
          <!-- 验证结果提示 -->
          <ValidationResult 
            v-if="validationResult"
            :result="validationResult"
            style="margin-top: 16px"
          />
          
          <!-- 验证结果对比 -->
          <ValidationCompare 
            v-if="validationResult && selectedWorker"
            :validation-result="validationResult"
            :output-params="outputParams"
            :response="modelResponse"
            style="margin-top: 16px"
          />
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick } from 'vue';
import { message } from 'ant-design-vue';
import HistoryPlanSelector from '../../components/PromptSimulator/HistoryPlanSelector.vue';
import WorkerSelector from '../../components/PromptSimulator/WorkerSelector.vue';
import InputForm from '../../components/PromptSimulator/InputForm.vue';
import WorkerKnowledgeTypes from '../../components/PromptSimulator/WorkerKnowledgeTypes.vue';
import QueriedKnowledgeList from '../../components/PromptSimulator/QueriedKnowledgeList.vue';
import MergedKnowledgeList from '../../components/PromptSimulator/MergedKnowledgeList.vue';
import ExtraFieldsList from '../../components/PromptSimulator/ExtraFieldsList.vue';
// ModelSelector 已移除，改为内联实现
import OutputSchema from '../../components/PromptSimulator/OutputSchema.vue';
import PromptViewer from '../../components/PromptSimulator/PromptViewer.vue';
import ResponseViewer from '../../components/PromptSimulator/ResponseViewer.vue';
import ValidationResult from '../../components/PromptSimulator/ValidationResult.vue';
import ValidationCompare from '../../components/PromptSimulator/ValidationCompare.vue';
import { filterKnowledge, generatePrompt, callModel, validateResponse, getWorkerDetail, getConfigKnowledge, mergeKnowledge, getKnowledgeDetails, getModels } from '../../api/promptSimulator';
import { getCallPlanLogs } from '../../api/callPlan';
import { buildVehicleInfoForKnowledgeMatch } from './vehicleInfoBuilder';

export default {
  name: 'PromptSimulator',
  components: {
    HistoryPlanSelector,
    WorkerSelector,
    InputForm,
    WorkerKnowledgeTypes,
    QueriedKnowledgeList,
    MergedKnowledgeList,
    ExtraFieldsList,
    OutputSchema,
    PromptViewer,
    ResponseViewer,
    ValidationResult,
    ValidationCompare
  },
  setup() {
    // 状态管理
    const selectedFlow = ref(null);
    const selectedStep = ref(null);
    const selectedWorker = ref(null);
    
    // 历史模式相关状态
    const historyMode = ref(false);
    const historySchemeSnapshot = ref(null);
    const historyPlanData = ref(null);
    const historyPlan = ref(null);
    const historyPlanLogs = ref([]);
    const inputFormData = ref({
      fault_phenomenon_id: null,
      fault_phenomenon: null,
      control_unit: {
        type_id: null,
        brand_id: null,
        model_id: null,
        type_name: null,
        brand_name: null,
        model_name: null,
        tag_value: null
      },
      control_units: [],
      engine_info_id: null,
      engine_info: null,
      vehicle_info: {
        type_id: null,
        brand_id: null,
        model_id: null,
        type_name: null,
        brand_name: null,
        model_name: null,
        tag_value: null
      },
      subsystem_info: {
        type_id: null,
        brand_id: null,
        model_id: null,
        type_name: null,
        brand_name: null,
        model_name: null,
        tag_value: null
      },
      component_info: {
        type_id: null,
        brand_id: null,
        model_id: null,
        type_name: null,
        brand_name: null,
        model_name: null,
        tag_value: null
      },
      system_voltage_id: null,
      system_voltage: null
    });
    const configKnowledge = ref(null); // Worker配置中的知识
    const queriedKnowledge = ref(null); // 查询到的知识
    const debugSQL = ref(null); // 搜索SQL调试信息
    const mergedKnowledge = ref(null); // 合并后的知识
    const selectedModelId = ref(null);
    const outputParams = ref(null);
    const workerDetail = ref(null);
    const xmlPrompt = ref('');
    const knowledgeMapping = ref([]);
    const responseSchema = ref(null);
    const selectedKnowledgeId = ref(null);
    const selectedPlaceholder = ref(null); // 选中的占位符
    const generating = ref(false);
    const callingModel = ref(false);
    const modelResponse = ref(null);
    const validationResult = ref(null);
    const promptViewerRef = ref(null);
    const promptKey = ref(0); // 用于强制重新渲染 PromptViewer 组件
    const historyPlanSelectorRef = ref(null);
    
    // 模型相关
    const availableModels = ref([]);
    const modelsLoading = ref(false);
    const STRUCTURED_OUTPUT_SOURCES = ['google_ai_studio', 'vertex_ai', 'doubao', 'openrouter'];
    
    // 模型来源显示名称映射
    const SOURCE_NAMES = {
      google_ai_studio: 'Google AI Studio',
      vertex_ai: 'Vertex AI',
      openrouter: 'OpenRouter',
      doubao: '豆包',
      cloudace: 'CloudAce'
    };
    
    // 获取模型显示名称
    const getModelDisplayName = (model) => {
      const sourceName = SOURCE_NAMES[model.source] || model.source;
      return `${sourceName} - ${model.model_id}`;
    };

    const selectedModel = computed(() => (
      availableModels.value.find(model => model.id === selectedModelId.value) || null
    ));

    const selectedModelStructuredOutputSupported = computed(() => (
      !!selectedModel.value && STRUCTURED_OUTPUT_SOURCES.includes(selectedModel.value.source)
    ));

    const selectedModelSubmitHint = computed(() => {
      if (!selectedModel.value) {
        return '';
      }

      const modelName = getModelDisplayName(selectedModel.value);

      if (selectedModelStructuredOutputSupported.value) {
        if (responseSchema.value) {
          return `${modelName} 支持结构化输出，提交时会把出参定义作为 response_schema 传给模型。`;
        }
        return `${modelName} 支持结构化输出，但当前未生成 response_schema，本次仍按普通文本请求。`;
      }

      if (responseSchema.value) {
        return `${modelName} 不支持结构化输出，本次主要依赖提示词约束输出，并在返回后做格式校验。`;
      }

      return `${modelName} 不支持结构化输出，本次将按普通文本请求。`;
    });
    
    // 加载可用模型列表
    const loadAvailableModels = async () => {
      modelsLoading.value = true;
      try {
        const result = await getModels();
        if (result.code === 200) {
          // result.data 是一个对象，包含 list 属性（数组）
          const models = result.data?.list || result.data || [];
          availableModels.value = Array.isArray(models) ? models : [];
          // 默认选中第一个模型
          if (availableModels.value.length > 0 && !selectedModelId.value) {
            selectedModelId.value = availableModels.value[0].id;
          }
        }
      } catch (error) {
        console.error('加载模型列表失败:', error);
      } finally {
        modelsLoading.value = false;
      }
    };

    // 计算属性
    const canGenerate = computed(() => {
      // 历史模式：只需要选择了worker即可（数据已从历史填充）
      if (historyMode.value) {
        return selectedWorker.value !== null && selectedWorker.value !== undefined;
      }
      // 手动模式：需要选择worker且填写故障现象
      return selectedWorker.value && inputFormData.value.fault_phenomenon_id;
    });
    
    // Worker配置的模型显示
    const workerModelDisplay = computed(() => {
      if (!workerDetail.value || !workerDetail.value.modelId) {
        return '';
      }
      // 在可用模型列表中查找对应的模型
      const model = availableModels.value.find(m => m.id === workerDetail.value.modelId);
      if (model) {
        return getModelDisplayName(model);
      }
      return `模型ID: ${workerDetail.value.modelId}`;
    });

    const historyPlanLogMap = computed(() => {
      const map = new Map();
      (historyPlanLogs.value || []).forEach(log => {
        if (log.step_level !== 2) {
          return;
        }
        const key = `${log.step_order}.${log.sub_step_order}`;
        map.set(key, log);
      });
      return map;
    });

    const availableWorkerRefs = computed(() => {
      if (!historyMode.value || !historySchemeSnapshot.value?.steps || !selectedWorker.value) {
        return [];
      }

      const flattenedWorkers = historySchemeSnapshot.value.steps.flatMap((stepInfo, stepIndex) => {
        const stepOrder = stepInfo?.step_order ?? (stepIndex + 1);
        const workers = Array.isArray(stepInfo?.sub_steps) ? stepInfo.sub_steps : [];

        return workers.map((worker, workerIndex) => ({
          step_order: stepOrder,
          sub_step_order: worker?.step_order ?? (workerIndex + 1),
          worker_id: worker?.id,
          worker_name: worker?.step_name || worker?.name || `Worker ${stepOrder}.${worker?.step_order ?? (workerIndex + 1)}`
        }));
      });

      const currentWorker = flattenedWorkers.find(worker => String(worker.worker_id) === String(selectedWorker.value));
      if (!currentWorker) {
        return [];
      }

      return flattenedWorkers
        .filter(worker => {
          if (worker.step_order < currentWorker.step_order) {
            return true;
          }
          if (worker.step_order === currentWorker.step_order) {
            return worker.sub_step_order < currentWorker.sub_step_order;
          }
          return false;
        })
        .sort((a, b) => {
          if (a.step_order !== b.step_order) {
            return a.step_order - b.step_order;
          }
          return a.sub_step_order - b.sub_step_order;
        })
        .map(worker => {
          const refKey = `${worker.step_order}.${worker.sub_step_order}`;
          const log = historyPlanLogMap.value.get(refKey) || null;
          return {
            placeholder: `{worker:${refKey}}`,
            display_name: `${refKey} ${worker.worker_name}`,
            worker_id: worker.worker_id,
            step_order: worker.step_order,
            sub_step_order: worker.sub_step_order,
            log_id: log?.id || null,
            status: log?.status || 'missing',
            value: log?.response_data ?? null
          };
        });
    });

    // 监听Worker选择
    watch(selectedWorker, async (newWorkerId) => {
      if (newWorkerId) {
        await loadWorkerDetail(newWorkerId);
        // 如果已有输入参数，自动触发知识匹配
        if (inputFormData.value.fault_phenomenon_id) {
          await matchKnowledge();
        }
      } else {
        workerDetail.value = null;
        outputParams.value = null;
      }
    });

    // 监听输入参数变化（仅用于数据同步，不用于触发匹配）
    // 匹配应该由子组件的@change事件触发
    // 这样，如果子组件判断不应该触发匹配（如：控制单元还有下级数据），
    // 就不会调用handleChange()，也就不会触发@change事件，父组件就不会触发匹配
    watch(() => inputFormData.value, () => {
      // 仅用于数据同步，不触发匹配
      // 匹配由子组件的@change事件通过handleInputChange触发
    }, { deep: true });

    const loadHistoryPlanLogs = async (planId) => {
      if (!planId) {
        historyPlanLogs.value = [];
        return;
      }

      try {
        const result = await getCallPlanLogs(planId);
        if (result.code === 200 && Array.isArray(result.data)) {
          historyPlanLogs.value = result.data;
        } else {
          historyPlanLogs.value = [];
          message.warning(result.message || '加载历史计划执行日志失败');
        }
      } catch (error) {
        console.error('加载历史计划执行日志失败:', error);
        historyPlanLogs.value = [];
        message.error('加载历史计划执行日志失败: ' + error.message);
      }
    };

    // 加载Worker详情
    const loadWorkerDetail = async (workerId) => {
      try {
        const result = await getWorkerDetail(workerId);
        if (result.code === 200) {
          // 确保 knowledgeTypes 是数组
          const data = {
            ...result.data,
            knowledgeTypes: Array.isArray(result.data.knowledgeTypes) 
              ? result.data.knowledgeTypes 
              : []
          };
          workerDetail.value = data;
          outputParams.value = data.outputParams;
        }
      } catch (error) {
        console.error('加载Worker详情失败:', error);
        workerDetail.value = null;
        outputParams.value = null;
      }
    };

    // Worker选择处理
    const handleWorkerSelected = async (workerId) => {
      selectedWorker.value = workerId;
      // 获取Worker配置中的知识
      if (workerId) {
        await loadConfigKnowledge(workerId);
      } else {
        configKnowledge.value = null;
      }
    };

    // 加载Worker配置中的知识
    const loadConfigKnowledge = async (workerId) => {
      try {
        const result = await getConfigKnowledge(workerId);
        if (result.code === 200) {
          configKnowledge.value = result.data;
        } else {
          configKnowledge.value = null;
          message.error(result.message || '获取配置知识失败');
        }
      } catch (error) {
        console.error('加载配置知识失败:', error);
        configKnowledge.value = null;
        message.error('加载配置知识失败: ' + error.message);
      }
    };

    // 输入参数变化处理（由子组件的@change事件触发）
    let changeDebounceTimer = null;
    const handleInputChange = () => {
      // 防抖处理
      if (changeDebounceTimer) {
        clearTimeout(changeDebounceTimer);
      }
      changeDebounceTimer = setTimeout(() => {
        if (selectedWorker.value && inputFormData.value.fault_phenomenon_id) {
          matchKnowledge();
        }
      }, 500);
    };

    // 匹配知识
    const matchKnowledge = async () => {
      if (!selectedWorker.value) return;
      
      try {
        const vehicleInfo = buildVehicleInfoForKnowledgeMatch({
          inputFormData: inputFormData.value,
          historyMode: historyMode.value,
          historyPlanData: historyPlanData.value
        });

        // 1. 搜索知识
        const filterResult = await filterKnowledge({
          worker_id: selectedWorker.value,
          vehicle_info: vehicleInfo
        });

        if (filterResult.code !== 200) {
          message.error(filterResult.message || '搜索知识失败');
          queriedKnowledge.value = null;
          debugSQL.value = null;
          mergedKnowledge.value = null;
          return;
        }

        queriedKnowledge.value = filterResult.data.queried_knowledge || {};
        debugSQL.value = filterResult.data.debug_sql || null;

        // 2. 自动合并知识
        const mergeResult = await mergeKnowledge({
          worker_id: selectedWorker.value,
          queried_knowledge: queriedKnowledge.value
        });

        if (mergeResult.code !== 200) {
          message.error(mergeResult.message || '合并知识失败');
          mergedKnowledge.value = null;
          return;
        }

        mergedKnowledge.value = mergeResult.data;

        // 3. 自动获取知识详情
        const detailsResult = await getKnowledgeDetails({
          knowledge_ids_by_type: mergeResult.data.merged_knowledge_ids
        });

        if (detailsResult.code !== 200) {
          message.error(detailsResult.message || '获取知识详情失败');
          return;
        }

        // 更新合并后的知识详情
        if (mergedKnowledge.value) {
          mergedKnowledge.value.knowledge_details = detailsResult.data.knowledge_by_type;
        }
      } catch (error) {
        console.error('匹配知识失败:', error);
        message.error('匹配知识失败: ' + error.message);
        queriedKnowledge.value = null;
        debugSQL.value = null;
        mergedKnowledge.value = null;
      }
    };

    // 知识点击处理
    const handleKnowledgeClick = (knowledgeId) => {
      selectedKnowledgeId.value = knowledgeId;
      selectedPlaceholder.value = null; // 清除占位符高亮
      if (promptViewerRef.value) {
        promptViewerRef.value.scrollToKnowledge(knowledgeId);
      }
    };

    // 生成提示词
    const handleGeneratePrompt = async () => {
      if (!selectedWorker.value) {
        message.warning('请先选择Worker');
        return;
      }

      // 手动模式需要验证故障现象ID
      if (!historyMode.value && !inputFormData.value.fault_phenomenon_id) {
        message.warning('请先填写故障现象');
        return;
      }

      generating.value = true;
      // 先清空旧数据，确保新数据能正确显示
      xmlPrompt.value = '';
      knowledgeMapping.value = [];
      responseSchema.value = null;
      modelResponse.value = null;
      validationResult.value = null;
      
      try {
        const vehicleInfo = {
          fault_phenomenon_id: inputFormData.value.fault_phenomenon_id,
          // 将control_unit对象展开为扁平结构（类型→品牌→型号）
          control_unit_type_id: inputFormData.value.control_unit?.type_id || null,
          control_unit_brand_id: inputFormData.value.control_unit?.brand_id || null,
          control_unit_model_id: inputFormData.value.control_unit?.model_id || null,
          engine_info_id: inputFormData.value.engine_info_id,
          // 将vehicle_info对象展开为扁平结构（类型→品牌→型号）
          vehicle_type_id: inputFormData.value.vehicle_info?.type_id || null,
          vehicle_brand_id: inputFormData.value.vehicle_info?.brand_id || null,
          vehicle_model_id: inputFormData.value.vehicle_info?.model_id || null,
          // 将subsystem_info对象展开为扁平结构（类型→品牌→型号）
          subsystem_type_id: inputFormData.value.subsystem_info?.type_id || null,
          subsystem_brand_id: inputFormData.value.subsystem_info?.brand_id || null,
          subsystem_model_id: inputFormData.value.subsystem_info?.model_id || null,
          // 将component_info对象展开为扁平结构（类型→品牌→型号）
          component_type_id: inputFormData.value.component_info?.type_id || null,
          component_brand_id: inputFormData.value.component_info?.brand_id || null,
          component_model_id: inputFormData.value.component_info?.model_id || null,
          system_voltage_id: inputFormData.value.system_voltage_id
        };

        // 历史模式：添加额外字段用于提示词替换
        if (historyMode.value && historyPlanData.value) {
          vehicleInfo.fault_phenomenon = historyPlanData.value.fault_phenomenon;
          vehicleInfo.control_unit = historyPlanData.value.control_unit_tag_value;
          vehicleInfo.identified_system = historyPlanData.value.identified_system;
          vehicleInfo.csv_data_stream = historyPlanData.value.csv_data_stream;
          vehicleInfo.working_conditions = historyPlanData.value.working_conditions;
          vehicleInfo.system_voltage = historyPlanData.value.system_voltage;
          // 传入完整的标签数组（由 planDataService.assembleTagData 组装，含子系统及其下属零件）
          vehicleInfo.subsystems = historyPlanData.value.subsystems || [];
          vehicleInfo.controlUnits = historyPlanData.value.controlUnits || [];
          vehicleInfo.components = historyPlanData.value.components || [];
          vehicleInfo.engines = historyPlanData.value.engines || [];
          vehicleInfo.vehicles = historyPlanData.value.vehicles || [];
          
          // 解析JSON字段
          if (historyPlanData.value.fault_code_list) {
            try {
              vehicleInfo.fault_code_list = typeof historyPlanData.value.fault_code_list === 'string' 
                ? JSON.parse(historyPlanData.value.fault_code_list) 
                : historyPlanData.value.fault_code_list;
            } catch (e) {
              vehicleInfo.fault_code_list = historyPlanData.value.fault_code_list;
            }
          }
          
          // 优先使用 selected_streams，向后兼容 stream_list
          const streamsData = historyPlanData.value.selected_streams || historyPlanData.value.stream_list;
          if (streamsData) {
            try {
              vehicleInfo.selected_streams = typeof streamsData === 'string'
                ? JSON.parse(streamsData)
                : streamsData;
            } catch (e) {
              vehicleInfo.selected_streams = streamsData;
            }
          }
        }

        // 优先使用前端已查询合并的知识（所见即所得），如果未查询则后端内部查询
        const requestParams = {
          worker_id: selectedWorker.value,
          vehicle_info: vehicleInfo,
          ref_plan_id: historyMode.value ? historyPlan.value?.id || null : null
        };
        
        // 如果已经查询并合并了知识，传入后端直接使用
        if (mergedKnowledge.value && mergedKnowledge.value.knowledge_details) {
          requestParams.knowledge_by_type = mergedKnowledge.value.knowledge_details;
        }
        
        const result = await generatePrompt(requestParams);

        if (result.code === 200) {
          xmlPrompt.value = result.data.xml_prompt || '';
          knowledgeMapping.value = result.data.knowledge_mapping || [];
          responseSchema.value = result.data.response_schema || null;
          promptKey.value += 1; // 更新 key 以强制重新渲染组件
          
          message.success('提示词生成成功');
        } else {
          message.error(result.message || '生成提示词失败');
        }
      } catch (error) {
        console.error('生成提示词失败:', error);
        message.error('生成提示词失败: ' + error.message);
      } finally {
        generating.value = false;
      }
    };

    // 调用模型
    const handleCallModel = async () => {
      if (!selectedModelId.value) {
        message.warning('请先选择模型');
        return;
      }

      if (!xmlPrompt.value) {
        message.warning('请先生成提示词');
        return;
      }

      callingModel.value = true;
      modelResponse.value = null;
      validationResult.value = null;

      try {
        const result = await callModel({
          model_id: selectedModelId.value,
          prompt_content: xmlPrompt.value,
          response_schema: responseSchema.value
        });

        if (result.code === 200) {
          // 解析响应
          let responseData = result.data.response;
          try {
            responseData = JSON.parse(responseData);
          } catch (e) {
            // 如果不是JSON，保持原样
          }
          modelResponse.value = responseData;

          // 自动触发验证
          if (outputParams.value) {
            await validateModelResponse(responseData);
          }
        } else {
          message.error(result.message || '调用模型失败');
        }
      } catch (error) {
        console.error('调用模型失败:', error);
        message.error('调用模型失败: ' + error.message);
      } finally {
        callingModel.value = false;
      }
    };

    // 验证模型响应
    const validateModelResponse = async (response) => {
      if (!outputParams.value) return;

      try {
        const result = await validateResponse({
          response: response,
          output_params: outputParams.value
        });

        if (result.code === 200) {
          validationResult.value = result.data;
        }
      } catch (error) {
        console.error('验证响应失败:', error);
      }
    };

    // 历史计划选择处理
    const handlePlanSelected = async (planDetail) => {
      try {
        // 打印完整的历史记录数据，方便调试比对
        console.log('========== 历史记录数据 ==========');
        console.log('完整 planDetail:', JSON.parse(JSON.stringify(planDetail)));
        console.log('planDetail.plan:', JSON.parse(JSON.stringify(planDetail.plan)));
        console.log('planDetail.plan_data:', JSON.parse(JSON.stringify(planDetail.plan_data)));
        console.log('================================');
        
        historyMode.value = true;
        historySchemeSnapshot.value = planDetail.scheme_snapshot;
        historyPlanData.value = planDetail.plan_data;
        historyPlan.value = planDetail.plan || null;
        await loadHistoryPlanLogs(historyPlan.value?.id || null);

        // 如果有计划数据，填充表单
        if (planDetail.plan_data) {
          fillFormFromPlanData(planDetail.plan_data);
        } else {
          console.warn('planDetail.plan_data 为空，无法填充表单');
        }

        message.success('历史计划加载成功');
      } catch (error) {
        console.error('处理历史计划失败:', error);
        message.error('处理历史计划失败: ' + error.message);
      }
    };

    // 从计划数据填充表单
    const fillFormFromPlanData = (planData) => {
      // 打印原始 planData，方便调试
      console.log('========== 填充表单数据 ==========');
      console.log('原始 planData:', JSON.parse(JSON.stringify(planData)));
      
      const parseArrayField = (value) => {
        if (Array.isArray(value)) return value;
        if (typeof value === 'string' && value.trim()) {
          try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
          } catch (error) {
            return [];
          }
        }
        return [];
      };

      const historyControlUnits = parseArrayField(planData.controlUnits);

      // 优先从新的数组格式提取数据，如果没有则使用旧的扁平字段（向后兼容）
      // 1. 控制单元（controlUnits 数组 -> control_unit）
      let controlUnit = {
        type_id: null,
        brand_id: null,
        model_id: null,
        type_name: null,
        brand_name: null,
        model_name: null,
        tag_value: null
      };
      
      if (historyControlUnits.length > 0) {
        // 使用新的数组格式（取第一个）
        const cu = historyControlUnits[0];
        controlUnit = {
          type_id: cu.typeId || null,
          brand_id: cu.brandId || null,
          model_id: cu.modelId || null,
          type_name: cu.typeName || null,
          brand_name: cu.brandName || null,
          model_name: cu.modelName || null,
          tag_value: cu.tagValue || null
        };
      } else {
        // 向后兼容：使用旧的扁平字段
        controlUnit = {
          type_id: planData.control_unit_type_id || null,
          brand_id: planData.control_unit_brand_id || null,
          model_id: planData.control_unit_model_id || null,
          type_name: planData.control_unit_type || null,
          brand_name: planData.control_unit_brand || null,
          model_name: planData.control_unit_model || null,
          tag_value: planData.control_unit_tag_value || null
        };
      }
      
      // 2. 发动机（engines 数组 -> engine_info）
      let engine_info_id = null;
      let engine_info = null;
      if (planData.engines && planData.engines.length > 0) {
        const engine = planData.engines[0];
        engine_info_id = engine.tagId || null;
        engine_info = engine.tagValue || null;
      } else {
        engine_info_id = planData.engine_tag_id || null;
        engine_info = planData.engine_tag_value || null;
      }
      
      // 3. 整车（vehicles 数组 -> vehicle_info）
      let vehicle_info = {
        type_id: null,
        brand_id: null,
        model_id: null,
        type_name: null,
        brand_name: null,
        model_name: null,
        tag_value: null
      };
      
      if (planData.vehicles && planData.vehicles.length > 0) {
        const vehicle = planData.vehicles[0];
        vehicle_info = {
          type_id: vehicle.typeId || null,
          brand_id: vehicle.brandId || null,
          model_id: vehicle.modelId || null,
          type_name: vehicle.typeName || null,
          brand_name: vehicle.brandName || null,
          model_name: vehicle.modelName || null,
          tag_value: vehicle.tagValue || null
        };
      } else {
        vehicle_info = {
          type_id: planData.vehicle_type_id || null,
          brand_id: planData.vehicle_brand_id || null,
          model_id: planData.vehicle_model_id || null,
          type_name: planData.vehicle_type_name || null,
          brand_name: planData.vehicle_brand_name || null,
          model_name: planData.vehicle_model_name || null,
          tag_value: planData.vehicle_tag_value || null
        };
      }
      
      // 4. 子系统（subsystems 数组 -> subsystem_info，取第一个）
      let subsystem_info = {
        type_id: null,
        brand_id: null,
        model_id: null,
        type_name: null,
        brand_name: null,
        model_name: null,
        tag_value: null
      };
      
      if (planData.subsystems && planData.subsystems.length > 0) {
        const subsystem = planData.subsystems[0];
        subsystem_info = {
          type_id: subsystem.typeId || null,
          brand_id: subsystem.brandId || null,
          model_id: subsystem.modelId || null,
          type_name: subsystem.typeName || null,
          brand_name: subsystem.brandName || null,
          model_name: subsystem.modelName || null,
          tag_value: subsystem.tagValue || null
        };
      } else {
        subsystem_info = {
          type_id: planData.subsystem_type_id || null,
          brand_id: planData.subsystem_brand_id || null,
          model_id: planData.subsystem_model_id || null,
          type_name: planData.subsystem_type_name || null,
          brand_name: planData.subsystem_brand_name || null,
          model_name: planData.subsystem_model_name || null,
          tag_value: planData.subsystem_tag_value || null
        };
      }
      
      // 5. 部件（components 数组 -> component_info，取第一个）
      let component_info = {
        type_id: null,
        brand_id: null,
        model_id: null,
        type_name: null,
        brand_name: null,
        model_name: null,
        tag_value: null
      };
      
      if (planData.components && planData.components.length > 0) {
        const component = planData.components[0];
        component_info = {
          type_id: component.typeId || null,
          brand_id: component.brandId || null,
          model_id: component.modelId || null,
          type_name: component.typeName || null,
          brand_name: component.brandName || null,
          model_name: component.modelName || null,
          tag_value: component.tagValue || null
        };
      } else {
        component_info = {
          type_id: planData.component_type_id || null,
          brand_id: planData.component_brand_id || null,
          model_id: planData.component_model_id || null,
          type_name: planData.component_type_name || null,
          brand_name: planData.component_brand_name || null,
          model_name: planData.component_model_name || null,
          tag_value: planData.component_tag_value || null
        };
      }
      
      const formData = {
        fault_phenomenon_id: planData.fault_phenomenon_id || null,
        fault_phenomenon: planData.fault_phenomenon || null,
        control_unit: controlUnit,
        control_units: historyControlUnits,
        engine_info_id: engine_info_id,
        engine_info: engine_info,
        vehicle_info: vehicle_info,
        subsystem_info: subsystem_info,
        component_info: component_info,
        system_voltage_id: planData.system_voltage_id || null,
        system_voltage: planData.system_voltage || null
      };
      
      // 打印填充后的表单数据
      console.log('========== 填充后的表单数据 ==========');
      console.log('inputFormData:', JSON.parse(JSON.stringify(formData)));
      console.log('================================');
      
      inputFormData.value = formData;
    };

    // 清除历史计划
    const handlePlanCleared = () => {
      historyMode.value = false;
      historySchemeSnapshot.value = null;
      historyPlanData.value = null;
      historyPlan.value = null;
      historyPlanLogs.value = [];
      
      // 清空表单（包括文本字段）
      inputFormData.value = {
        fault_phenomenon_id: null,
        fault_phenomenon: null,
        control_unit: { 
          type_id: null, 
          brand_id: null, 
          model_id: null,
          type_name: null,
          brand_name: null,
          model_name: null,
          tag_value: null
        },
        control_units: [],
        engine_info_id: null,
        engine_info: null,
        vehicle_info: { 
          type_id: null, 
          brand_id: null, 
          model_id: null,
          type_name: null,
          brand_name: null,
          model_name: null,
          tag_value: null
        },
        subsystem_info: { 
          type_id: null, 
          brand_id: null, 
          model_id: null,
          type_name: null,
          brand_name: null,
          model_name: null,
          tag_value: null
        },
        component_info: { 
          type_id: null, 
          brand_id: null, 
          model_id: null,
          type_name: null,
          brand_name: null,
          model_name: null,
          tag_value: null
        },
        system_voltage_id: null,
        system_voltage: null
      };
      
      // 清空其他状态
      selectedFlow.value = null;
      selectedStep.value = null;
      selectedWorker.value = null;
      queriedKnowledge.value = null;
      mergedKnowledge.value = null;
      xmlPrompt.value = '';
      modelResponse.value = null;
      validationResult.value = null;
    };

    // 清空所有配置数据（包括历史模式和模拟模式）
    const handleClearAllConfig = () => {
      // 清空历史模式相关数据
      historyMode.value = false;
      historySchemeSnapshot.value = null;
      historyPlanData.value = null;
      historyPlan.value = null;
      historyPlanLogs.value = [];
      
      // 清空流程和Worker选择
      selectedFlow.value = null;
      selectedStep.value = null;
      selectedWorker.value = null;
      
      // 清空输入表单数据
      inputFormData.value = {
        fault_phenomenon_id: null,
        fault_phenomenon: null,
        control_unit: { 
          type_id: null, 
          brand_id: null, 
          model_id: null,
          type_name: null,
          brand_name: null,
          model_name: null,
          tag_value: null
        },
        control_units: [],
        engine_info_id: null,
        engine_info: null,
        vehicle_info: { 
          type_id: null, 
          brand_id: null, 
          model_id: null,
          type_name: null,
          brand_name: null,
          model_name: null,
          tag_value: null
        },
        subsystem_info: { 
          type_id: null, 
          brand_id: null, 
          model_id: null,
          type_name: null,
          brand_name: null,
          model_name: null,
          tag_value: null
        },
        component_info: { 
          type_id: null, 
          brand_id: null, 
          model_id: null,
          type_name: null,
          brand_name: null,
          model_name: null,
          tag_value: null
        },
        system_voltage_id: null,
        system_voltage: null
      };
      
      // 清空知识相关数据
      configKnowledge.value = null;
      queriedKnowledge.value = null;
      mergedKnowledge.value = null;
      debugSQL.value = null;
      
      // 清空提示词和响应数据
      xmlPrompt.value = '';
      knowledgeMapping.value = [];
      responseSchema.value = null;
      modelResponse.value = null;
      validationResult.value = null;
      promptKey.value += 1;
      
      // 清空Worker详情
      workerDetail.value = null;
      outputParams.value = null;
      
      // 清空历史计划选择器的内部状态
      if (historyPlanSelectorRef.value) {
        historyPlanSelectorRef.value.clearSelection();
      }
      
      message.success('配置数据已清空');
    };

    // 额外字段点击处理（跳转到提示词中的占位符位置）
    const handleExtraFieldClick = (fieldInfo) => {
      selectedKnowledgeId.value = null; // 清除知识高亮
      
      // 如果有多个可能的占位符名称，尝试依次匹配
      const placeholdersToTry = fieldInfo.possiblePlaceholders || [fieldInfo.placeholder];
      selectedPlaceholder.value = placeholdersToTry[0]; // 设置第一个作为默认
      
      nextTick(() => {
        if (promptViewerRef.value) {
          // 尝试跳转到第一个匹配的占位符
          let found = false;
          for (const placeholder of placeholdersToTry) {
            if (promptViewerRef.value.scrollToPlaceholder(placeholder)) {
              found = true;
              selectedPlaceholder.value = placeholder; // 更新为实际匹配的占位符
              message.success(`已跳转到字段: ${fieldInfo.fieldName}`);
              break;
            }
          }
          if (!found) {
            message.warning(`未找到字段 ${fieldInfo.fieldName} 在提示词中的位置`);
          }
        }
      });
    };

    // 初始化加载模型列表
    loadAvailableModels();
    
    return {
      selectedFlow,
      selectedStep,
      selectedWorker,
      inputFormData,
      configKnowledge,
      queriedKnowledge,
      debugSQL,
      mergedKnowledge,
      selectedModelId,
      outputParams,
      workerDetail,
      xmlPrompt,
      knowledgeMapping,
      selectedKnowledgeId,
      selectedPlaceholder,
      generating,
      callingModel,
      modelResponse,
      validationResult,
      promptViewerRef,
      promptKey,
      canGenerate,
      historyMode,
      historySchemeSnapshot,
      historyPlanData,
      availableWorkerRefs,
      availableModels,
      modelsLoading,
      selectedModelStructuredOutputSupported,
      selectedModelSubmitHint,
      workerModelDisplay,
      getModelDisplayName,
      handleWorkerSelected,
      handleInputChange,
      handleKnowledgeClick,
      handleGeneratePrompt,
      handleCallModel,
      handlePlanSelected,
      handlePlanCleared,
      handleExtraFieldClick,
      handleClearAllConfig,
      historyPlanSelectorRef
    };
  }
};
</script>

<style scoped>
@import '../../styles/prompt-simulator.scss';

.prompt-simulator {
  padding: 16px;
}

.model-submit-hint {
  margin-top: -8px;
  margin-bottom: 8px;
}

/* 历史模式文字颜色：橙色 */
.mode-text-history {
  color: #fa8c16;
  font-weight: 600;
}

/* 模拟模式文字颜色：蓝色 */
.mode-text-simulate {
  color: #1890ff;
  font-weight: 600;
}
</style>

